import { useQuery } from '@tanstack/react-query';
import { useModeStore } from '../store/mode.store';
import { getSiteReadiness } from '../services/modeService';
import { useEffect } from 'react';
import { useSiteStore } from '../store/site.store';
import { SiteMode, SiteModeData } from '../types/mode.types';

const normalizeMode = (mode?: string): SiteMode => {
  const normalized = mode?.trim().replace(/-/g, '_').toUpperCase();
  return normalized === 'EARLY_STAGE' ? SiteMode.EARLY_STAGE : SiteMode.PERFORMANCE;
};

const createModeData = (siteMode: SiteMode): SiteModeData => ({
  mode: siteMode,
  reasonCodes: [],
  eligibleFeatures: siteMode === SiteMode.EARLY_STAGE 
    ? ['readiness_dashboard', 'action_queue', 'timeline', 'glossary']
    : ['full_analytics', 'experiments', 'opportunities', 'advanced_metrics'],
  disabledFeatures: siteMode === SiteMode.EARLY_STAGE
    ? ['advanced_analytics', 'experiments', 'opportunities']
    : [],
  siteAgeDays: 0,
  gscDataDays: 0,
  totalClicks: 0,
  totalImpressions: 0,
  gscDataStartDate: null,
  gscDataEndDate: null,
  ga4Connected: false,
  totalConversions: 0,
  readinessProgress: 0,
});

/**
 * Hook to fetch and manage site mode data
 * Uses mode from selectedSite (already loaded from /sites endpoint)
 */
export const useSiteMode = (_siteId: number | null | undefined) => {
  const { selectedSite } = useSiteStore();
  const {
    modeData,
    setModeData,
    setLoadingMode,
  } = useModeStore();

  const currentModeData = selectedSite?.mode
    ? createModeData(normalizeMode(selectedSite.mode))
    : modeData;

  // Create mode data from selectedSite.mode (no separate API call needed)
  useEffect(() => {
    if (selectedSite?.mode) {
      setModeData(createModeData(normalizeMode(selectedSite.mode)));
      setLoadingMode(false);
    }
  }, [selectedSite?.id, selectedSite?.mode, setModeData, setLoadingMode]);

  return {
    modeData: currentModeData,
    isLoading: false,
    error: null,
    refetch: () => {},
    isEarlyStageMode: currentModeData?.mode === SiteMode.EARLY_STAGE,
    isPerformanceMode: currentModeData?.mode === SiteMode.PERFORMANCE
  };
};

/**
 * Hook to fetch and manage site readiness data
 */
export const useSiteReadiness = (siteId: number | null | undefined) => {
  const {
    readinessData,
    setReadinessData,
    setLoadingReadiness,
    setReadinessError
  } = useModeStore();

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['site-readiness', siteId],
    queryFn: () => getSiteReadiness(siteId!),
    enabled: !!siteId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000 // 10 minutes
  });

  // Update store when data changes
  useEffect(() => {
    if (data) {
      setReadinessData(data);
    }
  }, [data, setReadinessData]);

  useEffect(() => {
    setLoadingReadiness(isLoading);
  }, [isLoading, setLoadingReadiness]);

  useEffect(() => {
    if (error) {
      setReadinessError(error.message);
    }
  }, [error, setReadinessError]);

  return {
    readinessData,
    isLoading,
    error,
    refetch
  };
};
